import liveServerStatus from '../../liveServerStatus';

/**
 * Generate HTML email template for leave request notifications
 * @param {Object} leaveData - The leave request data
 * @param {Object} userData - User data of the requester
 * @param {string} recipientName - Name of the email recipient
 * @param {string} status - Status of the leave request (pending, approved, rejected)
 * @returns {string} - HTML email content
 */
export const generateLeaveRequestEmailHTML = (
  leaveData,
  userData,
  recipientName,
  status = 'pending'
) => {
  // Set up variables based on status
  let subject, message, actionText, headerColor;
  
  // Extract first and last name from userData
  const fullName = userData.name || userData.displayName || userData.email || 'Employee';
  const nameParts = fullName.split(' ');
  const firstName = nameParts[0] || 'Employee';
  const lastName = nameParts.slice(1).join(' ') || '';
  
  const isManager = recipientName !== firstName;
  
  // Configure email content based on status and recipient
  switch (status) {
    case 'approved':
      headerColor = '#4caf50'; // green
      subject = 'Leave Request Approved';
      message = isManager 
        ? `You have approved the leave request from ${firstName} ${lastName}.`
        : 'Your leave request has been approved.';
      actionText = 'View Details';
      break;
    case 'rejected':
      headerColor = '#f44336'; // red
      subject = 'Leave Request Rejected';
      message = isManager 
        ? `You have rejected the leave request from ${firstName} ${lastName}.`
        : 'Your leave request has been rejected.';
      actionText = 'View Details';
      break;
    default: // pending
      headerColor = '#ff9800'; // orange
      subject = 'New Leave Request';
      message = isManager 
        ? `${firstName} ${lastName} has submitted a new leave request for your approval.`
        : 'Your leave request has been submitted successfully and is awaiting approval.';
      actionText = isManager ? 'Review Request' : 'View Request';
  }

  // Format date range for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  const dateRange = leaveData.startDate === leaveData.endDate
    ? formatDate(leaveData.startDate)
    : `${formatDate(leaveData.startDate)} - ${formatDate(leaveData.endDate)}`;

  // Add test environment indicator if not in live environment
  const testEnvironmentBanner = !liveServerStatus ? `
    <div style="background-color: #ffeb3b; color: #333; text-align: center; padding: 5px; font-weight: bold; font-size: 12px;">
      ⚠️ TEST EMAIL - No action required ⚠️
    </div>
  ` : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${subject}</title>
      <style>
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          line-height: 1.6;
          color: #333;
          margin: 0;
          padding: 0;
          background-color: #f5f5f5;
        }
        .email-container {
          max-width: 600px;
          margin: 0 auto;
          background-color: #ffffff;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
        }
        .email-header {
          background-color: ${headerColor};
          color: white;
          padding: 20px;
          text-align: center;
        }
        .email-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
        }
        .email-body {
          padding: 30px;
        }
        .greeting {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          color: #212121;
        }
        .message {
          margin-bottom: 25px;
          color: #424242;
        }
        .leave-details {
          background-color: #f9f9f9;
          border-left: 4px solid ${headerColor};
          padding: 15px;
          margin-bottom: 20px;
          border-radius: 4px;
        }
        .leave-details h3 {
          margin-top: 0;
          margin-bottom: 15px;
          color: #424242;
        }
        .leave-detail-item {
          margin-bottom: 10px;
        }
        .leave-detail-label {
          font-weight: 500;
          color: #616161;
        }
        .divider {
          height: 1px;
          background-color: #e0e0e0;
          margin: 25px 0;
        }
        .cta-button {
          display: inline-block;
          background-color: ${headerColor};
          color: white;
          text-decoration: none;
          padding: 12px 25px;
          border-radius: 4px;
          font-weight: 500;
          margin-top: 15px;
          text-align: center;
        }
        .email-footer {
          background-color: #212121;
          padding: 20px;
          text-align: center;
          color: #f5f5f5;
          font-size: 12px;
        }
        .company-name {
          font-weight: 600;
          color: #f5f5f5;
          margin-bottom: 8px;
        }
      </style>
    </head>
    <body>
      ${testEnvironmentBanner}
      <div class="email-container">
        <div class="email-header">
          <h1>${subject}</h1>
        </div>
        
        <div class="email-body">
          <div class="greeting">
            Hello ${recipientName || 'there'},
          </div>
          
          <div class="message">
            ${message}
          </div>
          
          <div class="leave-details">
            <h3>Leave Request Details</h3>
            <div class="leave-detail-item">
              <span class="leave-detail-label">Employee:</span> ${firstName} ${lastName}
            </div>
            <div class="leave-detail-item">
              <span class="leave-detail-label">Type:</span> ${leaveData.leaveType || leaveData.type || 'N/A'}
            </div>
            <div class="leave-detail-item">
              <span class="leave-detail-label">Date:</span> ${dateRange}
            </div>
            <div class="leave-detail-item">
              <span class="leave-detail-label">Duration:</span> ${leaveData.leaveDays || leaveData.duration || 0} ${(leaveData.leaveDays || leaveData.duration || 0) > 1 ? 'days' : 'day'}
            </div>
            ${(leaveData.leaveRemarks || leaveData.reason) ? `
            <div class="leave-detail-item">
              <span class="leave-detail-label">Remarks:</span> ${leaveData.leaveRemarks || leaveData.reason}
            </div>
            ` : ''}
            <div class="leave-detail-item">
              <span class="leave-detail-label">Status:</span> <strong style="color: ${headerColor};">${status.charAt(0).toUpperCase() + status.slice(1)}</strong>
            </div>
          </div>
          
          <a href="/leave-requests" class="cta-button">
            ${actionText}
          </a>
          
          <div class="divider"></div>
          
          <div class="message">
            If you need any assistance, please contact the HR team.
          </div>
        </div>
        
        <div class="email-footer">
          <div class="company-name">MyelinZ PPC Portal</div>
          <p>This is an automated message. Please do not reply directly to this email.</p>
          <p>© ${new Date().getFullYear()} MyelinZ. All rights reserved.</p>
          ${!liveServerStatus ? '<p><strong>TEST ENVIRONMENT - No action required</strong></p>' : ''}
        </div>
      </div>
    </body>
    </html>
  `;
};

export default generateLeaveRequestEmailHTML; 